<template>
  <div class="ap-overlay" ref="overlay" tabindex="-1" @keydown="onKeydown">

    <!-- Loading -->
    <div v-if="loading" class="ap-state-center">
      <rotate-square size="60px" />
      <p class="mt-3 ap-state-text">Carregando dados...</p>
    </div>

    <!-- Error -->
    <div v-else-if="erro" class="ap-state-center">
      <i class="fa fa-exclamation-circle fa-3x mb-3" style="color:#FF8F00"></i>
      <p class="ap-state-text mb-3">{{ erro }}</p>
      <button class="btn btn-light" @click="fechar">
        <i class="fa fa-arrow-left mr-1"></i> Voltar
      </button>
    </div>

    <!-- Presentation -->
    <template v-else>

      <!-- Header -->
      <div class="ap-header">
        <div class="ap-header-brand">
          <i class="fa fa-seedling mr-2" style="color:#4CAF50"></i>
          <span class="ap-header-title">Progressos das Metas</span>
        </div>

        <div class="ap-header-filters">
          <!-- Mode toggle -->
          <div class="ap-filter-group">
            <button
              :class="['ap-filter-btn', viewMode === 'metas' ? 'active' : '']"
              @click="viewMode = 'metas'"
              title="Visualizar progresso vs meta do período"
            ><i class="fa fa-bullseye mr-1"></i>Metas</button>
            <button
              :class="['ap-filter-btn', viewMode === 'anual' ? 'active' : '']"
              @click="viewMode = 'anual'"
              title="Visualizar evolução por ano"
            ><i class="fa fa-chart-line mr-1"></i>Por Ano</button>
          </div>

          <!-- Period filter (only in metas mode) -->
          <div v-if="viewMode === 'metas'" class="ap-filter-group">
            <button :class="['ap-filter-btn', yearFilter === 2 ? 'active' : '']" @click="yearFilter = 2">2 Anos</button>
            <button :class="['ap-filter-btn', yearFilter === 5 ? 'active' : '']" @click="yearFilter = 5">5 Anos</button>
            <button :class="['ap-filter-btn', yearFilter === 10 ? 'active' : '']" @click="yearFilter = 10">10 Anos</button>
          </div>
          <!-- Axis quick-jump (hidden on small screens) -->
          <div v-if="axes.length" class="ap-filter-group ap-axis-jumps">
            <button
              v-for="axis in axes"
              :key="axis.id"
              class="ap-filter-btn ap-axis-jump-btn"
              :class="{ active: slide.type === 'eixo' && slide.axis && slide.axis.id === axis.id }"
              @click="goToAxisSlide(axis.id)"
              :title="axis.name"
            >{{ axis.name }}</button>
          </div>
        </div>

        <div class="ap-header-right">
          <span v-if="viewMode === 'metas' && periodLabel" style="font-size:11px;color:rgba(255,255,255,0.65);white-space:nowrap;margin-right:12px">Período: {{ periodLabel }}</span>
          <span class="ap-counter">{{ currentSlide + 1 }}&nbsp;/&nbsp;{{ slides.length }}</span>
          <button class="ap-close-btn" @click="fechar" title="Fechar (Esc)">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Progress bar -->
      <div class="ap-progress-track">
        <div class="ap-progress-fill" :style="{ width: progress + '%' }"></div>
      </div>

      <!-- Slide area -->
      <div class="ap-content-area">
        <transition name="ap-fade" mode="out-in">
          <div :key="currentSlide" class="ap-slide-wrapper">

            <!-- ── COVER ── -->
            <div v-if="slide.type === 'cover'" class="ap-cover">
              <div class="ap-cover-icon"><i class="fa fa-seedling"></i></div>
              <h1 class="ap-cover-h1">Progressos das Metas</h1>
              <p v-if="contaNome" class="ap-cover-period">{{ contaNome }}</p>
              <div class="ap-cover-stats-row">
                <div class="ap-cover-stat-box">
                  <span class="ap-big-num">{{ axes.length }}</span>
                  <span class="ap-big-label">eixos estratégicos</span>
                </div>
                <div class="ap-cover-stat-sep"></div>
                <div class="ap-cover-stat-box">
                  <span class="ap-big-num">{{ programs.length }}</span>
                  <span class="ap-big-label">programas</span>
                </div>
              </div>
              <p class="ap-cover-hint">
                <i class="fa fa-arrow-right mr-1"></i>
                Use as setas do teclado ou os botões abaixo para navegar
              </p>
            </div>

            <!-- ── EIXOS OVERVIEW ── -->
            <div v-else-if="slide.type === 'eixos_overview'" class="ap-card">
              <div class="ap-card-header">
                <div class="ap-card-icon"><i class="fas fa-layer-group"></i></div>
                <div>
                  <h2 class="ap-card-title">Visão Geral por Eixo</h2>
                  <p class="ap-card-sub">
                    <span v-if="viewMode === 'metas'">Meta de {{ yearFilter }} anos — </span>
                    <span
                      class="font-weight-bold"
                      :class="totalPct >= 100 ? 'text-success' : totalPct >= 50 ? 'text-warning' : 'text-danger'"
                    >{{ totalPct }}%</span> atingido no total
                  </p>
                </div>
              </div>
              <div class="ap-axis-grid mt-3">
                <div
                  v-for="pc in pieChartsPerAxis"
                  :key="pc.axisId"
                  class="ap-axis-card"
                  @click="goToAxisSlide(pc.axisId)"
                  title="Clique para ver detalhes"
                >
                  <div class="ap-axis-card-name">{{ pc.axisName }}</div>
                  <div class="ap-axis-card-count">{{ pc.programCount }} programa{{ pc.programCount !== 1 ? 's' : '' }}</div>
                  <highcharts :options="pc.miniDonut" style="width:100%;" />
                  <div
                    class="ap-axis-pct"
                    :class="pc.pct >= 100 ? 'text-success' : pc.pct >= 50 ? 'text-warning' : 'text-danger'"
                  >{{ pc.pct }}%</div>
                  <div class="ap-axis-pct-label">da meta atingida</div>
                </div>
              </div>
            </div>

            <!-- ── EIXO ── -->
            <div v-else-if="slide.type === 'eixo'" class="ap-card">
              <div class="ap-card-header">
                <div class="ap-card-icon"><i class="fas fa-chart-bar"></i></div>
                <div>
                  <h2 class="ap-card-title">{{ slide.axis.name }}</h2>
                  <p class="ap-card-sub">
                    {{ slide.axisPrograms.length }} programa{{ slide.axisPrograms.length !== 1 ? 's' : '' }}
                    <span v-if="viewMode === 'metas'"> · Meta de {{ yearFilter }} anos</span>
                    <span v-else> · Evolução por ano</span>
                  </p>
                </div>
              </div>

              <!-- Metas mode -->
              <div v-if="viewMode === 'metas'" class="ap-chart-area">
                <div v-if="!slide.metasChart" class="ap-empty-chart">
                  <i class="fa fa-chart-bar fa-2x mb-2 d-block"></i>
                  Nenhuma meta definida neste eixo para o período selecionado.
                </div>
                <highcharts v-else :options="slide.metasChart" />
              </div>

              <!-- Anual mode -->
              <div v-else class="ap-chart-area">
                <div v-if="!slide.anualChart" class="ap-empty-chart">
                  <i class="fa fa-calendar-alt fa-2x mb-2 d-block"></i>
                  Nenhum registro de progresso encontrado.
                </div>
                <highcharts v-else :options="slide.anualChart" />
              </div>

              <!-- Public program logos -->
              <div v-if="slide.publicLogos && slide.publicLogos.length" class="ap-logos-section">
                <div class="ap-logos-label">
                  <i class="fa fa-link mr-1"></i>
                  Programas Públicos Vinculados
                </div>
                <div class="ap-logos-row">
                  <img
                    v-for="pp in slide.publicLogos"
                    :key="pp.id"
                    :src="pp.logo_url"
                    :alt="pp.name"
                    :title="pp.name"
                    class="ap-logo-img"
                  />
                </div>
              </div>
            </div>

            <!-- ── ANUAL CONSOLIDADO ── -->
            <div v-else-if="slide.type === 'anual_consolidado'" class="ap-card">
              <div class="ap-card-header">
                <div class="ap-card-icon"><i class="fas fa-calendar-alt"></i></div>
                <div>
                  <h2 class="ap-card-title">Evolução Anual</h2>
                  <p class="ap-card-sub">Total atingido por eixo ao longo dos anos</p>
                </div>
              </div>
              <div class="ap-chart-area">
                <div v-if="!anualConsolidadoChart" class="ap-empty-chart">
                  <i class="fa fa-calendar-alt fa-2x mb-2 d-block"></i>
                  Nenhum registro de progresso encontrado.
                </div>
                <highcharts v-else :options="anualConsolidadoChart" />
              </div>
            </div>

            <!-- ── CLOSING ── -->
            <div v-else-if="slide.type === 'closing'" class="ap-cover">
              <div class="ap-cover-icon" style="color:#4CAF50">
                <i class="fa fa-check-circle"></i>
              </div>
              <h1 class="ap-cover-h1">Plano de Ação em Andamento</h1>
              <div class="ap-cover-stats-row">
                <div class="ap-cover-stat-box">
                  <span
                    class="ap-big-num"
                    :style="{ color: totalPct >= 100 ? '#4CAF50' : totalPct >= 50 ? '#FF8F00' : '#E53935' }"
                  >{{ totalPct }}%</span>
                  <span class="ap-big-label">da meta de {{ yearFilter }} anos atingida</span>
                </div>
              </div>
              <button class="btn btn-light mt-4" @click="fechar">
                <i class="fa fa-arrow-left mr-1"></i> Voltar ao Plano de Ação
              </button>
            </div>

          </div>
        </transition>
      </div>

      <!-- Footer -->
      <div class="ap-footer">
        <button class="ap-nav-btn" :disabled="currentSlide === 0" @click="prevSlide" title="Anterior (←)">
          <i class="fa fa-chevron-left"></i>
        </button>
        <div v-if="slides.length <= 24" class="ap-dots">
          <span
            v-for="(s, i) in slides"
            :key="i"
            class="ap-dot"
            :class="{
              active: i === currentSlide,
              'is-special': s.type === 'cover' || s.type === 'closing',
              'is-overview': s.type === 'eixos_overview' || s.type === 'anual_consolidado'
            }"
            @click="goToSlide(i)"
            :title="slideTitle(s)"
          ></span>
        </div>
        <span v-else class="ap-counter" style="font-size:14px">
          {{ currentSlide + 1 }} de {{ slides.length }}
        </span>
        <button class="ap-nav-btn" :disabled="currentSlide === slides.length - 1" @click="nextSlide" title="Próximo (→)">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>

    </template>
  </div>
</template>

<script>
import RotateSquare from "../../components/RotateSquare";
import ActionPlanService from "../../servico/ActionPlanService";

const CHART_GREEN = "#5cb85c";
const CHART_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const AXIS_COLORS = ["#2E7D32", "#1565C0", "#6A1B9A", "#E65100", "#00838F", "#AD1457", "#33691E", "#4E342E"];

export default {
  name: "ActionPlanPresentation",
  components: { RotateSquare },

  data() {
    return {
      loading: true,
      erro: null,
      axes: [],
      programs: [],
      allProgressEntries: {},
      yearFilter: 2,
      viewMode: "metas",
      currentSlide: 0,
      windowHeight: window.innerHeight,
      planStartDate: "",
    };
  },

  computed: {
    contaId() {
      const auth = this.$store.getters.getAutenticacao;
      return auth && auth.contaSelecionada ? auth.contaSelecionada.id : null;
    },
    contaNome() {
      const auth = this.$store.getters.getAutenticacao;
      return auth && auth.contaSelecionada ? auth.contaSelecionada.apelido : null;
    },
    chartH() {
      return Math.max(300, this.windowHeight - 370);
    },
    goalField() {
      return this.yearFilter === 2 ? "goal_2yr" : this.yearFilter === 5 ? "goal_5yr" : "goal_10yr";
    },
    latestProgress() {
      const map = {};
      for (const [id, entries] of Object.entries(this.allProgressEntries)) {
        const filtered = this._filterByPeriod(entries, this.yearFilter);
        if (filtered.length) map[id] = filtered[0].achieved_value;
      }
      return map;
    },
    periodLabel() {
      if (!this.planStartDate) return "";
      const start = new Date(this.planStartDate + "T00:00:00");
      const end = new Date(start.getFullYear() + this.yearFilter, start.getMonth(), start.getDate());
      const fmt = (d) => d.toLocaleDateString("pt-BR", { month: "short", year: "numeric" });
      return `${fmt(start)} → ${fmt(end)}`;
    },
    allYears() {
      const years = new Set();
      for (const entries of Object.values(this.allProgressEntries)) {
        for (const e of entries) {
          years.add(new Date(e.reference_date).getFullYear());
        }
      }
      return [...years].sort();
    },
    progressByYear() {
      const result = {};
      for (const [id, entries] of Object.entries(this.allProgressEntries)) {
        const byYear = {};
        for (const e of entries) {
          const year = new Date(e.reference_date).getFullYear();
          if (!byYear[year] || new Date(e.reference_date) > new Date(byYear[year].reference_date)) {
            byYear[year] = e;
          }
        }
        result[id] = {};
        for (const [year, entry] of Object.entries(byYear)) {
          result[id][year] = entry.achieved_value;
        }
      }
      return result;
    },
    totalPct() {
      const goalField = this.goalField;
      const totalGoal = this.programs.reduce((sum, p) => sum + (p[goalField] || 0), 0);
      const totalAchieved = this.programs.reduce((sum, p) => sum + (this.latestProgress[p.id] || 0), 0);
      if (totalGoal === 0) return totalAchieved > 0 ? 100 : 0;
      return Math.round((totalAchieved / totalGoal) * 100);
    },
    pieChartsPerAxis() {
      const goalField = this.goalField;
      const result = [];
      for (const axis of this.axes) {
        const progs = this.programs.filter(p => p.strategic_axis_id === axis.id);
        if (!progs.length) continue;
        const achieved = progs.reduce((sum, p) => sum + (this.latestProgress[p.id] || 0), 0);
        const goal = progs.reduce((sum, p) => sum + (p[goalField] || 0), 0);
        if (achieved === 0 && goal === 0) continue;
        const pct = goal > 0 ? Math.round((achieved / goal) * 100) : (achieved > 0 ? 100 : 0);
        const achievedDisplay = goal > 0 ? Math.min(achieved, goal) : achieved;
        const remaining = Math.max(0, goal - achieved);
        const pieData = [];
        if (achievedDisplay > 0) pieData.push({ name: "Atingido", y: achievedDisplay, color: CHART_GREEN });
        if (remaining > 0) {
          pieData.push({ name: "Restante", y: remaining, color: "#e9ecef" });
        } else if (achievedDisplay === 0) {
          pieData.push({ name: "Sem progresso", y: goal > 0 ? goal : 1, color: "#e9ecef" });
        }
        result.push({
          axisId: axis.id,
          axisName: axis.name,
          programCount: progs.length,
          pct,
          miniDonut: {
            chart: { type: "pie", height: 140, margin: [8, 8, 8, 8], backgroundColor: null },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            tooltip: {
              formatter() { return `<b>${this.point.name}</b>: ${this.point.y}`; },
            },
            plotOptions: { pie: { innerSize: "60%", dataLabels: { enabled: false } } },
            series: [{ data: pieData }],
          },
        });
      }
      return result;
    },
    anualConsolidadoChart() {
      const years = this.allYears;
      if (!years.length) return null;
      const byYear = this.progressByYear;
      const series = [];
      for (const [idx, axis] of this.axes.entries()) {
        const progs = this.programs.filter(p => p.strategic_axis_id === axis.id);
        if (!progs.length) continue;
        const data = years.map(year =>
          progs.reduce((sum, p) => sum + ((byYear[p.id] || {})[year] || 0), 0)
        );
        if (!data.some(v => v > 0)) continue;
        series.push({
          name: axis.name,
          data,
          color: AXIS_COLORS[idx % AXIS_COLORS.length],
        });
      }
      if (!series.length) return null;
      return {
        chart: { type: "column", height: this.chartH, style: { fontFamily: CHART_FONT } },
        title: { text: null },
        credits: { enabled: false },
        xAxis: {
          categories: years.map(String),
          title: { text: "Ano" },
          labels: { style: { fontSize: "15px" } },
        },
        yAxis: {
          min: 0,
          title: { text: "Total Atingido" },
          labels: { style: { fontSize: "13px" } },
          gridLineColor: "#eee",
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
          itemStyle: { fontSize: "14px", fontFamily: CHART_FONT },
        },
        tooltip: {
          shared: true,
          formatter() {
            let s = `<b>${this.x}</b>`;
            this.points.forEach(pt => {
              s += `<br/><span style="color:${pt.color}">●</span> ${pt.series.name}: <b>${pt.y}</b>`;
            });
            return s;
          },
        },
        plotOptions: {
          column: {
            borderRadius: 3,
            dataLabels: {
              enabled: true,
              format: "{y}",
              style: { fontSize: "13px", fontWeight: "700", textOutline: "none" },
            },
          },
        },
        series,
      };
    },
    slides() {
      if (this.loading) return [{ type: "cover" }];
      const result = [];
      result.push({ type: "cover" });
      if (this.pieChartsPerAxis.length > 0) {
        result.push({ type: "eixos_overview" });
      }
      for (const axis of this.axes) {
        const axisPrograms = this.programs.filter(p => p.strategic_axis_id === axis.id);
        if (!axisPrograms.length) continue;
        const seen = new Set();
        const publicLogos = [];
        for (const prog of axisPrograms) {
          for (const pp of prog.linked_public_programs || []) {
            if (!seen.has(pp.id) && pp.logo_url) {
              seen.add(pp.id);
              publicLogos.push(pp);
            }
          }
        }
        result.push({
          type: "eixo",
          axis,
          axisPrograms,
          publicLogos,
          metasChart: this._buildMetasChart(axisPrograms),
          anualChart: this._buildAnualChart(axisPrograms),
        });
      }
      if (this.allYears.length > 0) {
        result.push({ type: "anual_consolidado" });
      }
      result.push({ type: "closing" });
      return result;
    },
    slide() {
      return this.slides[this.currentSlide] || {};
    },
    progress() {
      if (this.slides.length <= 1) return 100;
      return (this.currentSlide / (this.slides.length - 1)) * 100;
    },
  },

  beforeMount() {
    this.loadData();
  },

  mounted() {
    this._onResize = () => { this.windowHeight = window.innerHeight; };
    window.addEventListener("resize", this._onResize);
    this.$nextTick(() => {
      if (this.$refs.overlay) this.$refs.overlay.focus();
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
  },

  methods: {
    _filterByPeriod(entries, years) {
      const sorted = [...entries].sort((a, b) => new Date(b.reference_date) - new Date(a.reference_date));
      if (!this.planStartDate) return sorted;
      const start = new Date(this.planStartDate + "T00:00:00");
      const end = new Date(start.getFullYear() + years, start.getMonth(), start.getDate());
      return sorted.filter((e) => {
        const d = new Date(e.reference_date + "T00:00:00");
        return d >= start && d <= end;
      });
    },

    async loadData() {
      try {
        const contaId = this.contaId;
        if (!contaId) {
          this.erro = "Não foi possível identificar a conta.";
          return;
        }
        const [axesRes, progsRes, varsRes] = await Promise.all([
          ActionPlanService.ListAxes(contaId),
          ActionPlanService.ListProgramsWithLinks(contaId),
          ActionPlanService.GetVariables(contaId),
        ]);
        this.axes = axesRes.data;
        this.programs = progsRes.data;
        const psdVar = varsRes.data.find((v) => v.code === "plan_start_date");
        if (psdVar && psdVar.current_value) this.planStartDate = psdVar.current_value;

        if (this.programs.length) {
          const results = await Promise.all(
            this.programs.map(p =>
              ActionPlanService.ListProgress(contaId, p.id)
                .then(r => ({ id: p.id, data: r.data }))
                .catch(() => ({ id: p.id, data: [] }))
            )
          );
          const entries = {};
          for (const r of results) entries[r.id] = r.data;
          this.allProgressEntries = entries;
        }
      } catch (e) {
        this.erro = "Erro ao carregar os dados do Plano de Ação.";
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.overlay) this.$refs.overlay.focus();
        });
      }
    },

    _buildMetasChart(programs) {
      const goalField = this.goalField;
      const yearFilter = this.yearFilter;
      const progs = programs.filter(p => (p[goalField] || 0) > 0 || (this.latestProgress[p.id] || 0) > 0);
      if (!progs.length) return null;
      const categories = progs.map(p => p.name);
      const goalData = progs.map(p => p[goalField] || 0);
      const achievedData = progs.map(p => this.latestProgress[p.id] || 0);
      const maxVal = Math.max(1, ...goalData, ...achievedData);
      return {
        chart: {
          type: "bar",
          height: Math.max(260, progs.length * 54 + 100),
          style: { fontFamily: CHART_FONT },
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: { categories, title: null, labels: { style: { fontSize: "15px" } } },
        yAxis: {
          min: 0,
          max: maxVal,
          title: { text: "Valor" },
          labels: { style: { fontSize: "13px" } },
          gridLineColor: "#eee",
        },
        legend: { enabled: true, itemStyle: { fontSize: "14px", fontFamily: CHART_FONT } },
        tooltip: {
          shared: true,
          formatter() {
            let s = `<b>${this.x}</b>`;
            this.points.forEach(pt => {
              s += `<br/><span style="color:${pt.color}">●</span> ${pt.series.name}: <b>${pt.y}</b>`;
            });
            return s;
          },
        },
        plotOptions: {
          bar: {
            dataLabels: {
              enabled: true,
              format: "{y}",
              style: { fontSize: "14px", fontWeight: "700", textOutline: "none" },
            },
          },
        },
        series: [
          {
            name: `Meta ${yearFilter} anos`,
            data: goalData,
            color: "rgba(92,184,92,0.2)",
            borderColor: CHART_GREEN,
            borderWidth: 1,
          },
          {
            name: "Valor Atingido",
            data: achievedData,
            color: CHART_GREEN,
          },
        ],
      };
    },

    _buildAnualChart(programs) {
      const years = this.allYears;
      if (!years.length) return null;
      const byYear = this.progressByYear;
      const hasSomeData = programs.some(p =>
        years.some(y => ((byYear[p.id] || {})[y]) !== undefined)
      );
      if (!hasSomeData) return null;

      const categories = programs.map(p => p.name);
      const series = years.map((year, idx) => ({
        name: String(year),
        data: programs.map(p => (byYear[p.id] || {})[year] || 0),
        color: AXIS_COLORS[idx % AXIS_COLORS.length],
      }));
      const maxVal = Math.max(1, ...series.flatMap(s => s.data));

      return {
        chart: {
          type: "bar",
          height: Math.max(260, programs.length * 54 + 130),
          style: { fontFamily: CHART_FONT },
        },
        title: { text: null },
        credits: { enabled: false },
        xAxis: { categories, title: null, labels: { style: { fontSize: "15px" } } },
        yAxis: {
          min: 0,
          max: maxVal,
          title: { text: "Valor Atingido" },
          labels: { style: { fontSize: "13px" } },
          gridLineColor: "#eee",
        },
        legend: {
          enabled: true,
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
          itemStyle: { fontSize: "14px", fontFamily: CHART_FONT },
        },
        tooltip: {
          shared: true,
          formatter() {
            let s = `<b>${this.x}</b>`;
            this.points.forEach(pt => {
              s += `<br/><span style="color:${pt.color}">●</span> ${pt.series.name}: <b>${pt.y}</b>`;
            });
            return s;
          },
        },
        plotOptions: {
          bar: {
            grouping: true,
            dataLabels: {
              enabled: true,
              format: "{y}",
              style: { fontSize: "13px", fontWeight: "700", textOutline: "none" },
            },
          },
        },
        series,
      };
    },

    prevSlide() {
      if (this.currentSlide > 0) this.currentSlide--;
    },
    nextSlide() {
      if (this.currentSlide < this.slides.length - 1) this.currentSlide++;
    },
    goToSlide(i) {
      this.currentSlide = i;
    },
    goToAxisSlide(axisId) {
      const idx = this.slides.findIndex(s => s.type === "eixo" && s.axis && s.axis.id === axisId);
      if (idx >= 0) this.currentSlide = idx;
    },
    onKeydown(e) {
      if (["ArrowRight", "ArrowDown", " "].includes(e.key)) {
        e.preventDefault();
        this.nextSlide();
      } else if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        this.prevSlide();
      } else if (e.key === "Escape") {
        this.fechar();
      }
    },
    fechar() {
      this.$router.push({ name: "PmauActionPlan" });
    },
    slideTitle(s) {
      if (s.type === "cover") return "Capa";
      if (s.type === "eixos_overview") return "Visão Geral";
      if (s.type === "eixo") return s.axis ? s.axis.name : "Eixo";
      if (s.type === "anual_consolidado") return "Evolução Anual";
      if (s.type === "closing") return "Encerramento";
      return "";
    },
  },
};
</script>

<style scoped>
/* ── Overlay full-screen ────────────────────────────────────────── */
.ap-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0d2818;
  display: flex;
  flex-direction: column;
  outline: none;
  font-family: inherit;
}

/* ── Loading / Error ────────────────────────────────────────────── */
.ap-state-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.ap-state-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 15px;
}

/* ── Header ─────────────────────────────────────────────────────── */
.ap-header {
  flex-shrink: 0;
  min-height: 54px;
  background: #1B4332;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  gap: 12px;
  flex-wrap: wrap;
}
.ap-header-brand {
  display: flex;
  align-items: center;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}
.ap-header-title {
  white-space: nowrap;
}
.ap-header-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: center;
}
.ap-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.ap-counter {
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  white-space: nowrap;
}
.ap-close-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  line-height: 1;
}
.ap-close-btn:hover { color: #fff; background: rgba(255, 255, 255, 0.15); }

/* ── Filter buttons ─────────────────────────────────────────────── */
.ap-filter-group {
  display: flex;
  gap: 4px;
  background: rgba(0, 0, 0, 0.25);
  border-radius: 8px;
  padding: 3px;
}
.ap-filter-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
}
.ap-filter-btn:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
.ap-filter-btn.active { background: #4CAF50; color: #fff; font-weight: 600; }
.ap-axis-jumps { max-width: 480px; overflow: hidden; }
.ap-axis-jump-btn { font-size: 11px; padding: 3px 8px; }

/* ── Progress bar ───────────────────────────────────────────────── */
.ap-progress-track {
  flex-shrink: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.12);
}
.ap-progress-fill {
  height: 100%;
  background: #4CAF50;
  transition: width 0.3s ease;
}

/* ── Content area ───────────────────────────────────────────────── */
.ap-content-area {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 32px 0;
}
.ap-slide-wrapper {
  width: 100%;
  max-width: 1280px;
  display: flex;
  flex-direction: column;
  padding-bottom: 24px;
}

/* ── Transitions ────────────────────────────────────────────────── */
.ap-fade-enter-active { transition: all 0.22s ease; }
.ap-fade-leave-active { transition: all 0.18s ease; }
.ap-fade-enter, .ap-fade-leave-to { opacity: 0; transform: translateY(8px); }

/* ── Cover ──────────────────────────────────────────────────────── */
.ap-cover {
  text-align: center;
  padding: 40px 16px 56px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.ap-cover-icon {
  font-size: 72px;
  color: #4CAF50;
  margin-bottom: 24px;
}
.ap-cover-h1 {
  font-size: 42px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 12px;
  line-height: 1.25;
}
.ap-cover-period {
  color: rgba(255, 255, 255, 0.55);
  font-size: 18px;
  margin: 0 0 28px;
}
.ap-cover-stats-row {
  display: flex;
  align-items: stretch;
  gap: 0;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 16px;
  padding: 28px 48px;
  margin-bottom: 28px;
}
.ap-cover-stat-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 32px;
}
.ap-cover-stat-sep {
  width: 1px;
  background: rgba(76, 175, 80, 0.3);
  margin: 0;
}
.ap-big-num {
  font-size: 72px;
  font-weight: 800;
  color: #4CAF50;
  line-height: 1;
  display: block;
}
.ap-big-label {
  font-size: 16px;
  margin-top: 6px;
  display: block;
  color: rgba(255, 255, 255, 0.75);
}
.ap-cover-hint {
  color: rgba(255, 255, 255, 0.32);
  font-size: 15px;
  margin-top: 24px;
}

/* ── Cards ──────────────────────────────────────────────────────── */
.ap-card {
  background: #fff;
  border-radius: 20px;
  padding: 32px 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  flex: 1;
}
.ap-card-header {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 8px;
}
.ap-card-icon {
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  background: #E8F5E9;
  color: #2E7D32;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}
.ap-card-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a2a;
  margin: 0 0 4px;
  line-height: 1.35;
}
.ap-card-sub {
  font-size: 15px;
  color: #777;
  margin: 0;
}
.ap-chart-area { margin-top: 16px; }
.ap-empty-chart {
  text-align: center;
  color: #999;
  padding: 40px 0;
  font-size: 15px;
}

/* ── Eixos Overview grid ─────────────────────────────────────────── */
.ap-axis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
}
.ap-axis-card {
  border: 1px solid #e0e0e0;
  border-radius: 14px;
  padding: 16px 12px 12px;
  text-align: center;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s, transform 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.ap-axis-card:hover {
  box-shadow: 0 4px 18px rgba(46, 125, 50, 0.18);
  border-color: #81C784;
  transform: translateY(-2px);
}
.ap-axis-card-name {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a2a;
  line-height: 1.35;
  margin-bottom: 4px;
}
.ap-axis-card-count {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
}
.ap-axis-pct {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  margin-top: 4px;
}
.ap-axis-pct-label {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}

/* ── Logos section ──────────────────────────────────────────────── */
.ap-logos-section {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}
.ap-logos-label {
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}
.ap-logos-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}
.ap-logo-img {
  max-height: 52px;
  max-width: 120px;
  object-fit: contain;
  opacity: 0.85;
  transition: opacity 0.15s;
  filter: grayscale(20%);
}
.ap-logo-img:hover { opacity: 1; filter: none; }

/* ── Footer ─────────────────────────────────────────────────────── */
.ap-footer {
  flex-shrink: 0;
  height: 62px;
  background: #1B4332;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.ap-nav-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ap-nav-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.22); }
.ap-nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.ap-dots {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 560px;
  overflow: hidden;
  max-height: 30px;
}
.ap-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;
  flex-shrink: 0;
}
.ap-dot.active { background: #4CAF50; transform: scale(1.5); }
.ap-dot.is-special { background: rgba(255, 255, 255, 0.45); }
.ap-dot.is-overview { background: rgba(76, 175, 80, 0.55); }
.ap-dot:hover:not(.active) { background: rgba(255, 255, 255, 0.55); }

/* ── Responsive ─────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .ap-content-area { padding: 16px 12px 0; }
  .ap-card { padding: 20px 16px; }
  .ap-cover-h1 { font-size: 28px; }
  .ap-big-num { font-size: 52px; }
  .ap-cover-stats-row { padding: 20px 24px; }
  .ap-axis-jumps { display: none; }
}
</style>
